import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useNotification } from "hooks/useNotification";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import type { UniformFormType } from "types/uniformFormType";
import type { UniformItem } from "types/uniformItem";
import { transformUniformItemsResponse } from "utils/transformUniformItemsResponse";
import { useSportConfiguration } from "./useSportConfiguration";

export const useFetchUniforms = ({ entityId }: { entityId: string }) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["uniforms", sport, organizationId, entityId],
    queryFn: () => callApi(`/${sport}/o/${organizationId}/entities/${entityId}/uniforms?sortBy=nameLocal`),
  });
};

export const useFetchUniformItems = ({ entityId, uniformId }: { entityId: string; uniformId?: string }) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();
  const { configuration } = useSportConfiguration();

  const fn = async (): Promise<{
    uniformItems: UniformItem[];
    transformedParts: UniformFormType["uniformParts"];
  }> => {
    if (uniformId) {
      const url = `/${sport}/o/${organizationId}/entities/${entityId}/uniforms/${uniformId}/items`;
      const response = await callApi(url);
      const uniformItems: UniformItem[] = response.data ?? [];

      return {
        uniformItems,
        transformedParts: transformUniformItemsResponse(configuration.uniforms.items, uniformItems),
      };
    }

    return {
      uniformItems: [],
      transformedParts: {},
    };
  };

  return useQuery({
    queryKey: ["uniformItems", sport, organizationId, entityId, uniformId],
    queryFn: () => fn(),
  });
};

export const useUpsertUniform = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateAsync: upsertUniformItem } = useUpsertUniformItem();
  const { formatMessage } = useIntl();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({
      uniformId,
      entityId,
      upsertData,
    }: {
      uniformId?: string;
      entityId: string;
      upsertData: any;
      items: any[];
    }) => {
      let url = `/${sport}/o/${organizationId}/entities/${entityId}/uniforms`;
      if (uniformId) {
        url = `${url}/${uniformId}`;
      }
      return callApi<any>(url, {
        method: uniformId ? "PUT" : "POST",
        body: upsertData,
      });
    },
    onSuccess: async (
      response: any,
      {
        entityId,
        items,
      }: {
        uniformId?: string;
        entityId: string;
        upsertData: any;
        items: any[];
      }
    ) => {
      const uniformId = response?.data?.[0]?.uniformId;
      if (uniformId) {
        for (let i = 0; i < items?.length; i++) {
          const { uniformItemId, ...uniformItemUpsertData } = items[i];
          await upsertUniformItem({
            uniformId,
            uniformItemId,
            entityId,
            upsertData: uniformItemUpsertData,
          });
        }

        await queryClient.invalidateQueries({
          queryKey: ["uniformItems", sport, organizationId, uniformId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["uniforms", sport, organizationId, entityId],
        });

        showSuccessNotification(
          formatMessage({
            id: uniformId ? "notification.uniform.updated" : "notification.uniform.added",
          })
        );
      }
    },
  });
};

const useUpsertUniformItem = () => {
  const { sport, organizationId } = useParams();
  const callApi = useApi();

  return useMutation({
    mutationFn: async ({
      uniformId,
      uniformItemId,
      entityId,
      upsertData,
    }: {
      uniformId: string;
      uniformItemId?: string;
      entityId: string;
      upsertData: any;
    }) => {
      let url = `/${sport}/o/${organizationId}/entities/${entityId}/uniforms/${uniformId}/items`;
      if (uniformItemId) {
        url = `/${sport}/o/${organizationId}/entities/${entityId}/uniforms/items/${uniformItemId}`;
      }

      return await callApi(url, {
        method: uniformItemId ? "PUT" : "POST",
        body: upsertData,
      });
    },
  });
};

export const useDeleteUniform = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, uniformId }: { entityId: string; uniformId: string }) =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/uniforms/${uniformId}?forceDelete=true`, {
        method: "DELETE",
      }),
    onSuccess: async (response: any, { entityId, uniformId }: { entityId: string; uniformId: string }) => {
      if (response?.data && response.data.length > 0) {
        if (response?.data && response.data.length > 0 && response.data?.[0]?.success === true) {
          await queryClient.invalidateQueries({
            queryKey: ["uniformItems", sport, organizationId, uniformId],
          });
          await queryClient.invalidateQueries({
            queryKey: ["uniforms", sport, organizationId, entityId],
          });
          showSuccessNotification(formatMessage({ id: "notification.uniform.deleted" }));
        }
      }
    },
  });
};

export const useDeleteUniformItem = () => {
  const { sport, organizationId } = useParams();
  const callApi = useApi();

  return useMutation({
    mutationFn: ({ entityId, uniformItemId }: { entityId: string; uniformItemId: string }) =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/uniforms/items/${uniformItemId}`, {
        method: "DELETE",
      }),
  });
};
